<script setup lang="ts">
import IconChat from 'theme/components/atoms/icons/IconChat.vue';
import IconPeople from 'theme/components/atoms/icons/IconPeople.vue';
import ReviewScore from 'theme/components/molecules/ReviewScore.vue';
import PlannerThumbnail from '~/components/organisms/PlannerThumbnail/PlannerThumbnail.vue';
import { getGenerationString } from '~/entity/age';
import { type FragmentType, graphql } from '~/gql';

const props = defineProps<{
  planner: FragmentType<typeof PlannerProfileFragment>;
}>();

const PlannerProfileFragment = graphql(`
  fragment PlannerProfile on Planner {
    name
    avatar
    birthday
    gender
    review {
      score
      count
    }
    profile {
      publicAddress {
        prefecture
        city
      }
      hasChildren
    }
  }
`);

const planner = computed(() =>
  getFragment(PlannerProfileFragment, props.planner)
);

const generation = computed(() =>
  planner.value.birthday != null
    ? getGenerationString(fromDateString(planner.value.birthday))
    : undefined
);

const sexString = computed(() =>
  planner.value.gender === 'MALE' ? '男性' : '女性'
);

const publicAddressString = computed(() => {
  const { publicAddress } = planner.value.profile;

  if (!publicAddress) {
    return '';
  }

  return `${publicAddress.prefecture}${publicAddress.city}`;
});

const isReviewShown = computed(() => planner.value.review.count !== 0);
</script>

<template>
  <section :class="$style.planner">
    <PlannerThumbnail
      :img-path="planner.avatar"
      :class="$style.planner__thumbnail"
    />

    <div :class="[$style.planner__data, $style.data]">
      <section :class="[$style.data__attribute, $style.attribute]">
        <p v-if="generation" :class="$style.attribute__item">
          {{ generation }}
        </p>
        <p :class="$style.attribute__item">{{ sexString }}</p>
        <p :class="$style.attribute__item">{{ publicAddressString }}</p>
      </section>
      <h2 :class="$style.data__name">{{ planner.name }}</h2>

      <div :class="$style.box">
        <ReviewScore
          v-if="isReviewShown"
          :class="[$style.data__rating, $style.rating]"
          :rate="planner.review.score"
          :gap="6"
        />

        <p v-if="isReviewShown" :class="[$style.data__review, $style.review]">
          <IconChat :class="$style.review__iconChat" />
          <span :class="$style.review__title">クチコミ</span>
          <NuxtLink :to="{ hash: '#reviews' }" :class="$style.review__count">
            {{ planner.review.count }}
          </NuxtLink>
        </p>

        <p
          v-if="planner.profile.hasChildren"
          :class="[$style['children-badge']]"
        >
          <IconPeople />
          子育てプランナー
        </p>
      </div>
    </div>
  </section>
</template>
<style lang="scss" module>
.planner {
  $thumbnail-size: 100px;

  display: flex;
  align-items: center;

  &__thumbnail {
    width: $thumbnail-size;
    height: $thumbnail-size;
    flex-shrink: 0;
  }

  &__data {
    margin-left: 22px;
  }
}

.data {
  display: block;

  &__name {
    color: $color-text-and-object-high-emphasis;
    font-size: 24px;
    line-height: 1.2;
    font-weight: bold;

    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }

  &__rating {
    margin-top: 8px;
  }

  &__review {
    margin-top: 4px;
  }
}

.attribute {
  display: flex;
  font-size: 14px;

  &__item {
    &:nth-child(n + 2) {
      margin-left: 16px;
    }
  }
}

.rating {
  font-size: 14px;
}

.review {
  display: flex;
  align-items: center;

  &__iconChat {
    display: inline-block;
    font-size: 1.1em;
  }

  &__title {
    font-size: 12px;
    margin-left: 6px;
  }

  &__count {
    font-size: 12px;
    margin-left: 6px;
  }
}

.box {
  display: inline-block;
  padding-right: 22px;
}

.children-badge {
  margin-top: 8px;
  display: flex;
  font-weight: bold;
  font-size: 12px;
  line-height: 20px;
  text-align: left;
  border-radius: 10px;
  background-color: $color-surface-accent-primary-light;
  color: $color-text-and-object-accent-primary;
  padding: 1px 7px;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
</style>
